import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  FaMapMarkerAlt,
  FaRulerCombined,
  FaClock,
  FaArrowLeft,
  FaThumbsUp,
  FaThumbsDown,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock as FaStatusClock,
  FaUser,
  FaExpand,
  FaImage,
  FaComment,
  FaPaperPlane,
  FaUserCircle,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-hot-toast";
import {
  addPotholeComment,
  fetchAllComment,
  fetchSinglePothole,
  setAllComments,
  voteOnPothole,
} from "../../store/potholeSlice";
import { STATUSES } from "../../globals/misc/statuses";

const PotholeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { singlePothole, allComments, status, votingStatus } = useSelector(
    (state) => state.pothole
  );

  const [userVote, setUserVote] = useState(null);
  const [expandedImage, setExpandedImage] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  const isAuthenticated = Boolean(token || localStorage.getItem("token"));
  const isCurrent = singlePothole && singlePothole._id === id;

  useEffect(() => {
    setUserVote(null);
    setCommentText("");
    dispatch(setAllComments([]));
    dispatch(fetchSinglePothole(id));
    dispatch(fetchAllComment(id));
  }, [dispatch, id]);

  const imageUrl = singlePothole?._id === id ? singlePothole?.images?.url : null;

  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);

    if (!imageUrl) return undefined;

    let cancelled = false;
    const preload = new Image();
    preload.onload = () => {
      if (!cancelled) {
        setImageLoaded(true);
        setImageError(false);
      }
    };
    preload.onerror = () => {
      if (!cancelled) {
        setImageError(true);
        setImageLoaded(false);
      }
    };
    preload.src = imageUrl;
    if (preload.complete && preload.naturalWidth > 0) {
      setImageLoaded(true);
    }

    // Safety: never leave the spinner forever
    const timeout = setTimeout(() => {
      if (!cancelled) setImageLoaded(true);
    }, 2000);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [imageUrl]);

  const handleVote = async (voteType) => {
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/pothole/${id}` } });
      return;
    }

    try {
      await dispatch(voteOnPothole({ potholeId: id, voteType }));
      setUserVote((prev) => (prev === voteType ? null : voteType));
    } catch {
      /* toast handled in slice */
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/pothole/${id}` } });
      return;
    }
    if (!commentText.trim()) return;

    setCommentLoading(true);
    try {
      await dispatch(addPotholeComment({ potholeId: id, text: commentText }));
      setCommentText("");
      toast.success("Comment posted");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error posting comment");
    } finally {
      setCommentLoading(false);
    }
  };

  const severityBadge = (severity) => {
    switch (severity) {
      case "high":
        return "bg-red-500 text-white";
      case "medium":
        return "bg-amber-500 text-white";
      case "low":
        return "bg-emerald-500 text-white";
      default:
        return "bg-slate-500 text-white";
    }
  };

  const statusMeta = (value) => {
    switch (value) {
      case "in-progress":
        return {
          icon: <FaExclamationTriangle className="text-amber-500" />,
          className: "bg-amber-50 text-amber-800 border-amber-200",
        };
      case "verified":
        return {
          icon: <FaCheckCircle className="text-indigo-500" />,
          className: "bg-indigo-50 text-indigo-800 border-indigo-200",
        };
      case "fixed":
        return {
          icon: <FaCheckCircle className="text-emerald-500" />,
          className: "bg-emerald-50 text-emerald-800 border-emerald-200",
        };
      default:
        return {
          icon: <FaStatusClock className="text-navy-700" />,
          className: "bg-navy-900/5 text-navy-800 border-navy-900/10",
        };
    }
  };

  if (status === STATUSES.LOADING && !isCurrent) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-navy-800" />
      </div>
    );
  }

  if ((status === STATUSES.ERROR && !isCurrent) || (!isCurrent && !singlePothole)) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-2 text-navy-800 hover:text-amber-600"
        >
          <FaArrowLeft /> Back
        </button>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-10 text-center">
          <FaExclamationTriangle className="mx-auto mb-4 text-4xl text-red-500" />
          <p className="text-lg font-medium text-red-700">
            Failed to load pothole details
          </p>
        </div>
      </div>
    );
  }

  if (!singlePothole) return null;

  const statusInfo = statusMeta(singlePothole.status);
  const reporter =
    singlePothole.createdBy?.name ||
    (singlePothole.createdBy?._id
      ? `User #${singlePothole.createdBy._id.slice(-6)}`
      : "Anonymous");

  return (
    <div className="min-h-screen pb-16">
      {expandedImage && imageUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-navy-950/90 p-4 backdrop-blur-sm"
          onClick={() => setExpandedImage(false)}
        >
          <button
            type="button"
            className="absolute right-5 top-5 rounded-full bg-white/15 p-3 text-white hover:bg-white/25"
            onClick={() => setExpandedImage(false)}
            aria-label="Close image"
          >
            <FaTimes />
          </button>
          <img
            src={imageUrl}
            alt={`Pothole at ${singlePothole.address}`}
            className="max-h-[90vh] max-w-full rounded-xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 text-white">
        <div className="pointer-events-none absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_15%_20%,rgba(251,191,36,0.28),transparent_40%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-8 md:py-10">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center gap-2 text-sm text-white/75 transition hover:text-amber-300"
          >
            <FaArrowLeft /> Back to reports
          </button>

          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${severityBadge(
                singlePothole.severity
              )}`}
            >
              {singlePothole.severity || "unknown"} severity
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs capitalize backdrop-blur">
              {statusInfo.icon}
              {singlePothole.status?.replace("-", " ") || "unknown"}
            </span>
          </div>

          <h1 className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight md:text-5xl">
            {singlePothole.address || "Unknown location"}
          </h1>
          <p className="mt-3 max-w-2xl text-white/75">
            {singlePothole.description || "No description provided for this report."}
          </p>
        </div>
      </section>

      <div className="relative z-10 mx-auto max-w-6xl px-4 pb-4 pt-6 sm:pt-8">
        <div className="grid gap-6 lg:grid-cols-[1.35fr_0.85fr]">
          <div className="space-y-6">
            <div className="overflow-hidden rounded-3xl border border-navy-900/10 bg-white shadow-[0_20px_50px_rgba(11,31,68,0.1)]">
              <div className="relative h-56 overflow-hidden bg-surface sm:h-72 md:h-[26rem]">
                {imageUrl ? (
                  <>
                    {imageError ? (
                      <div className="flex h-full flex-col items-center justify-center text-navy-800/40">
                        <FaImage className="mb-2 text-4xl" />
                        <p>Failed to load image</p>
                      </div>
                    ) : (
                      <img
                        key={imageUrl}
                        src={imageUrl}
                        alt={`Pothole at ${singlePothole.address}`}
                        className="h-full w-full object-cover"
                        loading="eager"
                      />
                    )}
                    {!imageLoaded && !imageError && (
                      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-surface/55">
                        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-navy-800" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => setExpandedImage(true)}
                      className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-2 rounded-xl bg-navy-950/75 px-3 py-2 text-sm text-white backdrop-blur transition hover:bg-navy-950 sm:bottom-4 sm:right-4"
                    >
                      <FaExpand /> Expand
                    </button>
                  </>
                ) : (
                  <div className="flex h-full flex-col items-center justify-center text-navy-800/35">
                    <FaImage className="mb-2 text-4xl" />
                    <p>No image available</p>
                  </div>
                )}
              </div>

              <div className="p-6 md:p-8">
                <h2 className="font-display text-xl font-semibold text-navy-900">
                  Community votes
                </h2>
                <p className="mt-1 text-sm text-navy-800/65">
                  Help prioritize repairs by voting on this report.
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleVote("upvote")}
                    disabled={votingStatus === STATUSES.LOADING}
                    className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition ${
                      userVote === "upvote"
                        ? "bg-emerald-500 text-white shadow-md"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    } disabled:opacity-50`}
                  >
                    <FaThumbsUp />
                    {singlePothole.votes?.upvotes || 0}
                    <span className="text-sm font-medium">Upvote</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleVote("downvote")}
                    disabled={votingStatus === STATUSES.LOADING}
                    className={`inline-flex items-center gap-2 rounded-xl px-5 py-3 font-semibold transition ${
                      userVote === "downvote"
                        ? "bg-red-500 text-white shadow-md"
                        : "bg-red-50 text-red-600 hover:bg-red-100"
                    } disabled:opacity-50`}
                  >
                    <FaThumbsDown />
                    {singlePothole.votes?.downvotes || 0}
                    <span className="text-sm font-medium">Downvote</span>
                  </button>

                  {!isAuthenticated && (
                    <p className="text-sm text-navy-800/65">
                      <Link
                        to="/login"
                        state={{ from: `/pothole/${id}` }}
                        className="font-semibold text-amber-600 hover:text-amber-500"
                      >
                        Login
                      </Link>{" "}
                      to vote
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className="rounded-3xl border border-navy-900/10 bg-white p-6 shadow-[0_16px_40px_rgba(11,31,68,0.08)] md:p-8">
              <div className="mb-6 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-900/5 text-navy-800">
                  <FaComment />
                </div>
                <div>
                  <h2 className="font-display text-xl font-semibold text-navy-900">
                    Comments
                  </h2>
                  <p className="text-sm text-navy-800/60">
                    {allComments.length} conversation
                    {allComments.length === 1 ? "" : "s"}
                  </p>
                </div>
              </div>

              <form onSubmit={handleCommentSubmit} className="mb-6">
                <textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder={
                    isAuthenticated
                      ? "Share an update or note for others..."
                      : "Login to join the conversation"
                  }
                  rows="3"
                  disabled={commentLoading || !isAuthenticated}
                  className="w-full rounded-2xl border border-navy-900/10 bg-surface px-4 py-3 outline-none transition focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15 disabled:opacity-70"
                />
                <div className="mt-3 flex items-center justify-between gap-3">
                  {!isAuthenticated ? (
                    <p className="text-sm text-navy-800/65">
                      <Link
                        to="/login"
                        state={{ from: `/pothole/${id}` }}
                        className="font-semibold text-amber-600"
                      >
                        Login
                      </Link>{" "}
                      to comment
                    </p>
                  ) : (
                    <span className="text-sm text-navy-800/50">Be respectful</span>
                  )}
                  <button
                    type="submit"
                    disabled={
                      commentLoading || !commentText.trim() || !isAuthenticated
                    }
                    className="inline-flex items-center gap-2 rounded-xl bg-navy-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-800 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {commentLoading ? (
                      <span className="h-4 w-4 animate-spin rounded-full border-b-2 border-white" />
                    ) : (
                      <FaPaperPlane />
                    )}
                    Post
                  </button>
                </div>
              </form>

              {allComments.length === 0 ? (
                <div className="rounded-2xl bg-surface px-4 py-10 text-center">
                  <FaComment className="mx-auto mb-3 text-3xl text-navy-700/30" />
                  <p className="text-navy-800/65">
                    No comments yet. Start the discussion.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allComments.map((comment) => (
                    <div
                      key={comment._id}
                      className="rounded-2xl border border-navy-900/8 bg-surface/80 p-4"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy-900/10 text-navy-800">
                          <FaUserCircle className="text-xl" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span className="font-semibold text-navy-900">
                              {comment.user?.name ||
                                comment.user?.userName ||
                                "Anonymous"}
                            </span>
                            <span className="text-xs text-navy-800/50">
                              {new Date(comment.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <p className="mt-1 text-navy-800/85">{comment.text}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-3xl border border-navy-900/10 bg-white p-6 shadow-[0_16px_40px_rgba(11,31,68,0.08)]">
              <h3 className="font-display text-lg font-semibold text-navy-900">
                Report details
              </h3>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex gap-3 rounded-xl bg-surface p-3">
                  <FaMapMarkerAlt className="mt-0.5 text-amber-600" />
                  <div>
                    <p className="font-medium text-navy-900">Address</p>
                    <p className="text-navy-800/70">
                      {singlePothole.address || "Not available"}
                    </p>
                  </div>
                </li>
                {singlePothole.location?.coordinates && (
                  <li className="flex gap-3 rounded-xl bg-surface p-3">
                    <FaMapMarkerAlt className="mt-0.5 text-navy-700" />
                    <div>
                      <p className="font-medium text-navy-900">Coordinates</p>
                      <p className="text-navy-800/70">
                        {Number(singlePothole.location.coordinates[1]).toFixed(5)},{" "}
                        {Number(singlePothole.location.coordinates[0]).toFixed(5)}
                      </p>
                    </div>
                  </li>
                )}
                {(singlePothole.size?.width || singlePothole.size?.depth) && (
                  <li className="flex gap-3 rounded-xl bg-surface p-3">
                    <FaRulerCombined className="mt-0.5 text-amber-600" />
                    <div>
                      <p className="font-medium text-navy-900">Size</p>
                      <p className="text-navy-800/70">
                        {singlePothole.size?.width || "—"}cm ×{" "}
                        {singlePothole.size?.depth || "—"}cm
                      </p>
                    </div>
                  </li>
                )}
                <li className="flex gap-3 rounded-xl bg-surface p-3">
                  <FaUser className="mt-0.5 text-navy-700" />
                  <div>
                    <p className="font-medium text-navy-900">Reported by</p>
                    <p className="text-navy-800/70">{reporter}</p>
                  </div>
                </li>
                <li className="flex gap-3 rounded-xl bg-surface p-3">
                  <FaClock className="mt-0.5 text-navy-700" />
                  <div>
                    <p className="font-medium text-navy-900">Reported on</p>
                    <p className="text-navy-800/70">
                      {new Date(singlePothole.createdAt).toLocaleString()}
                    </p>
                  </div>
                </li>
              </ul>

              <div
                className={`mt-5 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold capitalize ${statusInfo.className}`}
              >
                {statusInfo.icon}
                {singlePothole.status?.replace("-", " ") || "unknown"}
              </div>
            </div>

            <div className="rounded-3xl bg-gradient-to-b from-navy-900 to-navy-800 p-6 text-white shadow-lg">
              <h3 className="font-display text-lg font-semibold">Take action</h3>
              <p className="mt-2 text-sm text-white/70">
                Spotted another hazard nearby? Add it so the community can track
                it.
              </p>
              <div className="mt-5 flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/report")}
                  className="rounded-xl bg-amber-500 px-4 py-3 font-semibold text-navy-950 transition hover:bg-amber-400"
                >
                  Report another pothole
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/potholes")}
                  className="rounded-xl border border-white/20 bg-white/5 px-4 py-3 font-semibold text-white transition hover:bg-white/10"
                >
                  Browse all reports
                </button>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default PotholeDetails;
